/**
 * DealDetailPanel — right-docked drawer for a single deal.
 *
 * Honours the global CLAUDE.md side-panel rule: NEVER a full-screen modal.
 * Desktop: 380px fixed right-dock that pushes inward as a flex sibling.
 * Mobile (<lg): bottom sheet with 3 snap points (peek / half / full).
 *
 * Multiple deals can be kept open as "drafts" by closing the panel without
 * clearing the selection — the parent stashes the id in a dock list which
 * renders as collapsed chips below this component.
 *
 * No `backdrop-blur` or `bg-black/60` overlay — desktop layout is
 * non-blocking; mobile uses a soft translucent backdrop only.
 */

import { useEffect, useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { X, MapPin, User, Phone, Building2, ChevronDown, Minimize2 } from 'lucide-react';
import { clsx } from 'clsx';
import type { DealWithSuburb } from '../../hooks/useDealsWithSuburbs';
import { verdictColour } from '../../utils/regionValidation';
import { hueFromId, monogram } from './InspectorChips';
import { Row, SectionLabel, SNAP_HEIGHTS, type SnapPoint } from './_shared/panelPrimitives';

interface Props {
  deal: DealWithSuburb | null;
  open: boolean;
  onClose: () => void;
  onMinimise: () => void;
}

export function DealDetailPanel({ deal, open, onClose, onMinimise }: Props) {
  const [snap, setSnap] = useState<SnapPoint>('half');

  // Reset to half-height every time a new deal slides in.
  useEffect(() => {
    if (deal) setSnap('half');
  }, [deal?.deal.id]);

  if (!deal) return null;

  const { deal: d, regionMatch, assignedInspector, lat, lng } = deal;
  const pinColour = verdictColour(regionMatch?.verdict?.label ?? null);

  return (
    <>
      {/* DESKTOP: inline right-docked panel. Flex sibling — pushes layout. */}
      <aside
        className={clsx(
          'hidden h-full shrink-0 overflow-hidden border-l border-white/10 bg-neutral-950/95 transition-all duration-300 lg:block',
          open ? 'w-[380px]' : 'w-0',
        )}
      >
        {open && (
          <DealPanelBody
            deal={deal}
            pinColour={pinColour}
            assignedInspector={assignedInspector}
            lat={lat}
            lng={lng}
            onClose={onClose}
            onMinimise={onMinimise}
          />
        )}
      </aside>

      {/* MOBILE: Radix Dialog as a bottom sheet — soft backdrop, snap points. */}
      <Dialog.Root open={open} onOpenChange={(o) => (!o ? onClose() : undefined)}>
        <Dialog.Portal>
          <Dialog.Overlay className="fixed inset-0 z-40 bg-neutral-950/40 lg:hidden" />
          <Dialog.Content
            className="fixed inset-x-0 bottom-0 z-50 flex flex-col rounded-t-2xl border-t border-white/10 bg-neutral-950 shadow-2xl transition-[height] duration-300 lg:hidden"
            style={{ height: SNAP_HEIGHTS[snap] }}
            onOpenAutoFocus={(e) => e.preventDefault()}
          >
            <div className="flex shrink-0 items-center justify-between border-b border-white/10 px-3 py-2">
              <div className="flex items-center gap-1">
                {(['peek', 'half', 'full'] as SnapPoint[]).map((s) => (
                  <button
                    key={s}
                    type="button"
                    onClick={() => setSnap(s)}
                    className={clsx(
                      'rounded-full px-2 py-0.5 text-[10px] font-medium uppercase tracking-wider',
                      snap === s ? 'bg-white/10 text-white' : 'text-neutral-500 hover:text-neutral-300',
                    )}
                  >
                    {s}
                  </button>
                ))}
              </div>
              <Dialog.Close asChild>
                <button type="button" className="rounded-lg p-1 text-neutral-500 hover:bg-white/5">
                  <X className="h-4 w-4" />
                </button>
              </Dialog.Close>
            </div>
            <Dialog.Title className="sr-only">{d.name}</Dialog.Title>
            <Dialog.Description className="sr-only">Deal detail</Dialog.Description>
            <div className="min-h-0 flex-1 overflow-y-auto">
              <DealPanelBody
                deal={deal}
                pinColour={pinColour}
                assignedInspector={assignedInspector}
                lat={lat}
                lng={lng}
                onClose={onClose}
                onMinimise={onMinimise}
                hideChrome
              />
            </div>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>
    </>
  );
}

interface BodyProps {
  deal: DealWithSuburb;
  pinColour: string;
  assignedInspector: DealWithSuburb['assignedInspector'];
  lat: number | null;
  lng: number | null;
  onClose: () => void;
  onMinimise: () => void;
  hideChrome?: boolean;
}

function DealPanelBody({
  deal,
  pinColour,
  assignedInspector,
  lat,
  lng,
  onClose,
  onMinimise,
  hideChrome,
}: BodyProps) {
  const { deal: d, regionMatch } = deal;

  return (
    <div className="flex h-full flex-col">
      {!hideChrome && (
        <div className="flex shrink-0 items-center justify-between border-b border-white/10 px-4 py-3">
          <div className="flex items-center gap-2">
            <span
              className="h-2.5 w-2.5 rounded-full"
              style={{ backgroundColor: pinColour, boxShadow: `0 0 0 3px ${pinColour}22` }}
            />
            <div className="text-[10px] font-medium uppercase tracking-widest text-neutral-500">
              Deal
            </div>
          </div>
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={onMinimise}
              className="rounded-lg p-1 text-neutral-500 hover:bg-white/5 hover:text-neutral-300"
              title="Minimise to dock"
            >
              <Minimize2 className="h-3.5 w-3.5" />
            </button>
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg p-1 text-neutral-500 hover:bg-white/5 hover:text-neutral-300"
              title="Close"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}

      <div className="min-h-0 flex-1 overflow-y-auto p-4">
        <h2 className="text-lg font-semibold leading-tight text-neutral-100">{d.name}</h2>
        {d.stageName && (
          <div className="mt-1 inline-flex items-center rounded-full border border-white/10 bg-white/[0.04] px-2 py-0.5 text-[11px] text-neutral-400">
            {d.stageName}
          </div>
        )}

        {/* Verdict + region card */}
        <div
          className="mt-4 rounded-xl border p-3"
          style={{
            borderColor: `${pinColour}40`,
            backgroundColor: `${pinColour}0c`,
          }}
        >
          <div className="text-[10px] font-medium uppercase tracking-widest" style={{ color: pinColour }}>
            Region match
          </div>
          {regionMatch?.zone ? (
            <>
              <div className="mt-1 text-sm font-medium text-neutral-100">
                {regionMatch.suburb ?? 'In service area'}
                {regionMatch.state ? `, ${regionMatch.state}` : ''}
              </div>
              <div className="mt-0.5 text-xs text-neutral-400">
                Zone {regionMatch.zone}
                {regionMatch.matchType !== 'none' && (
                  <span className="ml-1.5 text-neutral-600">· {regionMatch.matchType}</span>
                )}
              </div>
              {regionMatch.verdict?.label && (
                <div className="mt-2 inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-[11px] font-medium"
                  style={{ backgroundColor: `${pinColour}22`, color: pinColour }}
                >
                  {regionMatch.verdict.label}
                </div>
              )}
            </>
          ) : (
            <div className="mt-1 text-sm text-neutral-300">
              Outside known service zones.
              {regionMatch?.distanceKm != null && (
                <span className="ml-1 text-neutral-500">~{regionMatch.distanceKm}km from nearest.</span>
              )}
            </div>
          )}
        </div>

        {/* Contact section */}
        <div className="mt-4 space-y-2">
          <SectionLabel>Contact</SectionLabel>
          {d.contactName && (
            <Row icon={<User className="h-3.5 w-3.5" />} label={d.contactName} />
          )}
          {d.contactPhone && (
            <Row
              icon={<Phone className="h-3.5 w-3.5" />}
              label={d.contactPhone}
              href={`tel:${d.contactPhone}`}
            />
          )}
          {d.address && (
            <Row
              icon={<MapPin className="h-3.5 w-3.5" />}
              label={d.address}
              href={
                lat != null && lng != null
                  ? `https://www.google.com/maps?q=${lat},${lng}`
                  : `https://www.google.com/maps?q=${encodeURIComponent(d.address)}`
              }
              external
            />
          )}
          {!d.contactName && !d.contactPhone && !d.address && (
            <div className="text-xs text-neutral-500">No contact info on this deal.</div>
          )}
        </div>

        {/* Inspector */}
        <div className="mt-4 space-y-2">
          <SectionLabel>Suggested inspector</SectionLabel>
          {assignedInspector ? (
            <div className="flex items-center gap-2.5 rounded-xl border border-white/10 bg-white/[0.03] p-2.5">
              <span
                className="flex h-9 w-9 items-center justify-center rounded-full text-xs font-semibold text-white"
                style={{ backgroundColor: hueFromId(assignedInspector.id) }}
              >
                {monogram(assignedInspector.name)}
              </span>
              <div className="min-w-0 flex-1">
                <div className="truncate text-sm font-medium text-neutral-100">{assignedInspector.name}</div>
                <div className="text-[11px] text-neutral-500">
                  {assignedInspector.region_code ?? 'No region'}
                  {assignedInspector.email && ` · ${assignedInspector.email}`}
                </div>
              </div>
            </div>
          ) : (
            <div className="rounded-xl border border-dashed border-white/10 bg-white/[0.02] p-3 text-xs text-neutral-500">
              No inspector matched. Out-of-area deals need a manual assignment in Settings.
            </div>
          )}
        </div>

        {/* Money */}
        {d.amount != null && (
          <div className="mt-4">
            <SectionLabel>Value</SectionLabel>
            <div className="mt-1 text-2xl font-semibold tabular-nums text-neutral-100">
              ${d.amount.toLocaleString()}
            </div>
          </div>
        )}

        {/* Org from customFields */}
        {typeof d.customFields?.org_name === 'string' && (
          <Row
            icon={<Building2 className="h-3.5 w-3.5" />}
            label={d.customFields.org_name as string}
            className="mt-4"
          />
        )}

        {/* Raw custom fields (collapsed) */}
        {Object.keys(d.customFields ?? {}).length > 0 && (
          <details className="mt-6 group">
            <summary className="flex cursor-pointer list-none items-center gap-1 text-[11px] font-medium uppercase tracking-widest text-neutral-500 hover:text-neutral-300">
              <ChevronDown className="h-3 w-3 transition group-open:rotate-180" />
              Custom fields
            </summary>
            <dl className="mt-2 space-y-1 rounded-xl border border-white/5 bg-black/20 p-2 text-[11px]">
              {Object.entries(d.customFields).map(([k, v]) => (
                <div key={k} className="grid grid-cols-[1fr_2fr] gap-2">
                  <dt className="truncate text-neutral-500">{k}</dt>
                  <dd className="truncate text-neutral-300">{String(v)}</dd>
                </div>
              ))}
            </dl>
          </details>
        )}
      </div>
    </div>
  );
}
